import React from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, LANGUAGE_LABELS, SupportedLanguage } from '../i18n/config';

interface LanguageSwitcherProps {
    /** Kleurtoon voor de context waarin de switcher staat. */
    tone?: 'light' | 'dark';
    className?: string;
}

/**
 * Compacte segmented control om van taal te wisselen. Itereert over
 * SUPPORTED_LANGUAGES, dus nieuwe talen verschijnen automatisch. De keuze wordt
 * door i18next-browser-languagedetector in localStorage ('language') bewaard.
 */
const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ tone = 'light', className = '' }) => {
    const { t, i18n } = useTranslation();

    // Normaliseer bijv. 'en-US' naar 'en' zodat de juiste knop actief is.
    const current = (i18n.resolvedLanguage || i18n.language || '').split('-')[0];

    const changeLanguage = (lang: SupportedLanguage) => {
        if (lang !== current) {
            void i18n.changeLanguage(lang);
        }
    };

    const isDark = tone === 'dark';
    const containerClasses = isDark
        ? 'border-white/15 bg-white/10'
        : 'border-gray-200 bg-gray-100';

    return (
        <div
            role="group"
            aria-label={t('common.language')}
            className={`inline-flex items-center gap-0.5 rounded-lg border p-0.5 ${containerClasses} ${className}`}
        >
            {SUPPORTED_LANGUAGES.map((lang) => {
                const active = lang === current;
                const activeClasses = isDark
                    ? 'bg-white text-sidebar shadow-sm'
                    : 'bg-primary-600 text-white shadow-sm';
                const inactiveClasses = isDark
                    ? 'text-white/70 hover:text-white'
                    : 'text-gray-500 hover:text-gray-800';
                return (
                    <button
                        key={lang}
                        type="button"
                        onClick={() => changeLanguage(lang)}
                        aria-pressed={active}
                        title={LANGUAGE_LABELS[lang]}
                        className={`rounded-md px-2 py-1 text-xs font-semibold uppercase tracking-wide transition-colors ${active ? activeClasses : inactiveClasses}`}
                    >
                        {lang}
                    </button>
                );
            })}
        </div>
    );
};

export default LanguageSwitcher;
